/**
 * Voorinspectie List Page
 *
 * Shows all items awaiting voorinspectie (pre-inspection).
 * Items are filtered by: Voorinspectie_gedaan__c = false AND Status__c = 'Klaar voor Voorinspectie'
 *
 * Features:
 * - Search by item number (WI-xxxxx)
 * - Click item to go to inspection form
 * - Shows progress badge
 * - Caches items in IndexedDB for offline support
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, Button, Badge } from '../components/ui';
import { salesforceClient } from '../lib/salesforce';
import {
  getCachedVoorinspectieItems,
  cacheVoorinspectieItems,
  type CachedVoorinspectieItem,
} from '../lib/db/database';
import type { VoorinspectieItem } from '../lib/salesforce';
import { isWerkbank } from '../lib/uiFlag';
import { WbButton, WbChip, WbFlag, WbListRow, WbSearchField, WbSectionKop, WbState } from '../components/wb';
import { useHeader } from '../stores';
import { WachtrijMislukt } from '../components/WachtrijMislukt';
import { matchtZoek } from '../lib/utils/zoek';

/** Werkbank-render (plan 2026-09-02) of legacy; per toestel, zie lib/uiFlag. */
const WB = isWerkbank();

/** Cache bewaart createdDate als Date of als string; de UI wil altijd ISO. */
function alsIso(d: Date | string): string {
  return d instanceof Date ? d.toISOString() : d;
}

// Polling interval for real-time updates (60 seconds)
const POLLING_INTERVAL_MS = 60000;

// Group definition voor 4-secties dashboard.
// Sinds plan 2026-05-03-defer-voorinspectie-tot-akkoord: Akkoord-kleden krijgen
// een eigen sectie zodat wasserij ziet welke kleden geklopt + afgerond moeten worden.
type GroupKey = 'klaar' | 'wacht' | 'nee' | 'akkoord';

interface Group {
  key: GroupKey;
  label: string;
  emoji: string;
  description: string;
  items: VoorinspectieItem[];
}

function bucketFor(item: VoorinspectieItem): GroupKey {
  const status = item.risicoStatus;
  if (status === 'Niet_Akkoord') return 'nee';
  if (status === 'Gemeld' || status === 'Wacht_klant') return 'wacht';
  if (status === 'Akkoord') return 'akkoord';
  // Geen | Alternatief_gekozen | null → te inspecteren
  return 'klaar';
}

/** "wacht sinds X uur/dagen" badge voor Wacht_klant items */
function formatWaitTime(isoStart: string | null): string | null {
  if (!isoStart) return null;
  const ms = Date.now() - new Date(isoStart).getTime();
  if (ms <= 0) return null;
  const hours = Math.floor(ms / (1000 * 60 * 60));
  if (hours < 1) return 'wacht <1u';
  if (hours < 48) return `wacht ${hours}u`;
  const days = Math.floor(hours / 24);
  return `wacht ${days}d`;
}

export function VoorinspectieListPage() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [items, setItems] = useState<VoorinspectieItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch items from API or cache
  const fetchItems = useCallback(async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      // Try API first
      const response = await salesforceClient.getVoorinspectieItems();
      console.log('[VoorinspectieListPage] API response:', response);

      if (response.success && response.data) {
        const apiItems = response.data.items || [];
        setItems(apiItems);

        // Cache items for offline use
        const itemsToCache: CachedVoorinspectieItem[] = apiItems.map(item => ({
          itemId: item.itemId,
          itemName: item.itemName,
          productName: item.productName,
          werkOrderId: item.werkOrderId,
          werkOrderName: item.werkOrderName,
          companyCamProjectId: item.companyCamProjectId,
          createdDate: new Date(item.createdDate),
          lengthCm: item.lengthCm,
          widthCm: item.widthCm,
          surfaceM2: item.surfaceM2,
          soort: item.soort,
          merk: item.merk,
          diameterCm: item.diameterCm,
          vorm: item.vorm,
          aantal: item.aantal,
          wasrapport: item.wasrapport,
          secondaryItems: item.secondaryItems,
          isKlacht: item.isKlacht,
          klachtReden: item.klachtReden,
          _cachedAt: new Date(),
        }));
        await cacheVoorinspectieItems(itemsToCache);
      } else {
        // Try cache on API failure
        console.warn('[VoorinspectieListPage] API failed, trying cache');
        const cachedItems = await getCachedVoorinspectieItems();
        if (cachedItems.length > 0) {
          const mappedItems: VoorinspectieItem[] = cachedItems.map(item => ({
            itemId: item.itemId,
            itemName: item.itemName,
            productName: item.productName,
            werkOrderId: item.werkOrderId,
            werkOrderName: item.werkOrderName,
            companyCamProjectId: item.companyCamProjectId,
            createdDate: alsIso(item.createdDate),
            lengthCm: item.lengthCm,
            widthCm: item.widthCm,
            diameterCm: null,
            surfaceM2: item.surfaceM2,
            vorm: null,
            aantal: null,
            soort: item.soort,
            merk: item.merk,
            wasrapport: null,
            secondaryItems: [],
            // Risico-flow defaults — cache bevat geen risico-data (online-only in MVP)
            risicoStatus: 'Geen',
            risicoTypes: null,
            risicoToelichting: null,
            risicoGemeldOp: null,
            risicoBeslistOp: null,
            risicoKlantReactie: null,
            isKlacht: item.isKlacht,
            klachtReden: item.klachtReden,
          }));
          setItems(mappedItems);
        } else {
          setError(response.message || 'Kon items niet ophalen');
          setItems([]);
        }
      }
    } catch (err) {
      console.error('[VoorinspectieListPage] Error fetching items:', err);

      // Try cache on network error
      try {
        const cachedItems = await getCachedVoorinspectieItems();
        if (cachedItems.length > 0) {
          const mappedItems: VoorinspectieItem[] = cachedItems.map(item => ({
            itemId: item.itemId,
            itemName: item.itemName,
            productName: item.productName,
            werkOrderId: item.werkOrderId,
            werkOrderName: item.werkOrderName,
            companyCamProjectId: item.companyCamProjectId,
            createdDate: alsIso(item.createdDate),
            lengthCm: item.lengthCm,
            widthCm: item.widthCm,
            diameterCm: null,
            surfaceM2: item.surfaceM2,
            vorm: null,
            aantal: null,
            soort: item.soort,
            merk: item.merk,
            wasrapport: null,
            secondaryItems: [],
            // Risico-flow defaults — cache bevat geen risico-data (online-only in MVP)
            risicoStatus: 'Geen',
            risicoTypes: null,
            risicoToelichting: null,
            risicoGemeldOp: null,
            risicoBeslistOp: null,
            risicoKlantReactie: null,
            isKlacht: item.isKlacht,
            klachtReden: item.klachtReden,
          }));
          setItems(mappedItems);
          setError('Offline modus - gegevens kunnen verouderd zijn');
        } else {
          setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden');
          setItems([]);
        }
      } catch {
        setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden');
        setItems([]);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Fetch items on mount
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Polling for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchItems(true);
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchItems]);

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return items;
    }

    return items.filter(item => matchtZoek(searchQuery, item.itemName, item.werkOrderName, item.productName));
  }, [items, searchQuery]);

  // Group filtered items by risico-status (4 secties).
  // Volgorde = urgentie: actie-nodig eerst, "te inspecteren" laatst.
  const groups = useMemo<Group[]>(() => {
    const buckets: Record<GroupKey, VoorinspectieItem[]> = { klaar: [], wacht: [], nee: [], akkoord: [] };
    for (const item of filteredItems) {
      buckets[bucketFor(item)].push(item);
    }
    return [
      {
        key: 'nee',
        label: 'Klant zegt nee',
        emoji: '🔴',
        description: 'Actie nodig — bel klant of annuleer kleed',
        items: buckets.nee,
      },
      {
        key: 'wacht',
        label: 'Wacht op klant',
        emoji: '🟡',
        description: 'Mail verstuurd — wacht op reactie',
        items: buckets.wacht,
      },
      {
        key: 'akkoord',
        label: 'Klant gaf akkoord',
        emoji: '✅',
        description: 'Klop het kleed en rond de voorinspectie af',
        items: buckets.akkoord,
      },
      {
        key: 'klaar',
        label: 'Nog te inspecteren',
        emoji: '⚪',
        description: 'Geen risico gemeld — voorinspectie nog te doen',
        items: buckets.klaar,
      },
    ];
  }, [filteredItems]);

  // Handle refresh
  const handleRefresh = async () => {
    await fetchItems(true);
  };

  useHeader(WB ? 'Voorinspectie' : null, WB ? `${items.length}` : null);

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return 'Vandaag';
    if (isYesterday) return 'Gisteren';

    return new Intl.DateTimeFormat('nl-NL', {
      day: 'numeric',
      month: 'short',
    }).format(date);
  };

  // Format dimensions for display
  const formatDimensions = (item: VoorinspectieItem) => {
    if (item.lengthCm && item.widthCm) {
      return `${item.lengthCm} × ${item.widthCm} cm`;
    }
    if (item.surfaceM2) {
      return `${item.surfaceM2.toFixed(1)} m²`;
    }
    return 'Niet gemeten';
  };

  if (WB) {
    return (
      <div className="space-y-4">
        <WbSearchField value={searchQuery} onChange={setSearchQuery} placeholder="Zoek WI-nummer, order of product" />
        <WachtrijMislukt soort="voorinspectie" />

        {isLoading && <WbState variant="laden" titel="Items laden…" />}
        {!isLoading && error && items.length === 0 && <WbState variant="fout" titel={error} onOpnieuw={() => fetchItems()} />}
        {!isLoading && error && items.length > 0 && <WbFlag kind="opmerking">{error}</WbFlag>}
        {!isLoading && !error && items.length === 0 && <WbState variant="leeg" titel="Alle voorinspecties gedaan." />}
        {!isLoading && !error && items.length > 0 && filteredItems.length === 0 && (
          <WbState variant="leeg" titel={`Geen resultaten voor "${searchQuery}" in deze lijst.`}>
            <Link to={`/zoek?q=${encodeURIComponent(searchQuery)}`} className="wb-focus inline-flex min-h-tap items-center font-semibold underline underline-offset-4 text-wb-inkt">
              Zoek "{searchQuery}" in alle kleden
            </Link>
          </WbState>
        )}

        {!isLoading && groups.map((group) => {
          if (group.items.length === 0) return null;
          // Groeperen per werkorder: een WB met meer dan één kleed krijgt een dunne groepskop.
          const perWo = new Map<string, VoorinspectieItem[]>();
          for (const it of group.items) {
            const lijst = perWo.get(it.werkOrderName) ?? [];
            lijst.push(it);
            perWo.set(it.werkOrderName, lijst);
          }
          return (
            <section key={group.key} className="space-y-1.5">
              <WbSectionKop
                label={group.label}
                teller={group.items.length}
                uitleg={group.key !== 'klaar' ? group.description : null}
                actie={group.key === 'klaar' ? (
                  <WbButton variant="link" onClick={handleRefresh} disabled={isRefreshing}>
                    {isRefreshing ? 'Verversen…' : 'Ververs'}
                  </WbButton>
                ) : undefined}
              />
              {[...perWo.entries()].map(([wo, lijst]) => (
                <div key={wo} className="space-y-1.5">
                  {lijst.length > 1 && (
                    <p className="font-mono text-xs font-semibold text-wb-inkt2 pt-1">{wo} · {lijst.length} kleden</p>
                  )}
                  {lijst.map((item) => {
                    const wait = group.key === 'wacht' ? formatWaitTime(item.risicoGemeldOp) : null;
                    const maat = item.lengthCm && item.widthCm
                      ? `${item.lengthCm} × ${item.widthCm}`
                      : item.surfaceM2 ? `${item.surfaceM2.toFixed(1)} m²` : 'niet gemeten';
                    return (
                      <WbListRow
                        key={item.itemId}
                        onClick={() => navigate(`/voorinspectie/${item.itemId}`)}
                        hoofd={item.itemName}
                        chips={
                          <>
                            {item.isKlacht && <WbChip kind="klacht">Klacht</WbChip>}
                            {wait && <WbChip kind="wacht">{wait}</WbChip>}
                            {group.key === 'nee' && <WbChip kind="actie">Actie nodig</WbChip>}
                            {group.key === 'akkoord' && <WbChip kind="klaar">Klop + rond af</WbChip>}
                          </>
                        }
                        sub={`${maat} · ${item.werkOrderName} · ${formatDate(item.createdDate)}${item.soort ? ` · ${item.soort}` : ''}`}
                      />
                    );
                  })}
                </div>
              ))}
            </section>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-lg">
      {/* Header */}
      <section>
        <div className="flex items-center gap-sm mb-xs">
          <Link
            to="/"
            className="p-xs -ml-xs rounded-kleda-sm hover:bg-kleda-bg-clean transition-colors"
          >
            <svg className="w-5 h-5 text-kleda-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h2 className="font-heading text-xl font-bold text-kleda-text-main">
              Voorinspectie
            </h2>
            <p className="text-kleda-text-muted">
              {items.length} {items.length === 1 ? 'item' : 'items'} te inspecteren
            </p>
          </div>
        </div>
      </section>

      {/* Search bar */}
      <div className="relative">
        <svg
          className="absolute left-md top-1/2 -translate-y-1/2 w-5 h-5 text-kleda-text-muted pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          inputMode="numeric"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Zoek op WI-nummer, werkorder of product..."
          className="w-full pl-12 pr-md py-sm rounded-kleda-md border border-kleda-border bg-white text-kleda-text-main placeholder:text-kleda-text-muted focus:outline-none focus:ring-2 focus:ring-kleda-primary focus:border-transparent"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-md top-1/2 -translate-y-1/2 p-xs rounded-full hover:bg-kleda-bg-clean"
          >
            <svg className="w-4 h-4 text-kleda-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Refresh button */}
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="text-kleda-text-muted"
        >
          <svg
            className={`w-4 h-4 mr-xs ${isRefreshing ? 'animate-spin' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {isRefreshing ? 'Synchroniseren...' : 'Ververs'}
        </Button>
      </div>

      {/* Items list */}
      <div className="space-y-sm">
        {/* Loading state */}
        {isLoading && (
          <Card>
            <CardContent className="py-xl">
              <div className="flex flex-col items-center justify-center gap-md">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-kleda-primary border-t-transparent" />
                <p className="text-kleda-text-muted">Items laden...</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {!isLoading && error && items.length === 0 && (
          <Card variant="elevated">
            <CardContent className="py-lg">
              <div className="flex flex-col items-center justify-center gap-md text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-kleda-text-main">Fout bij laden</p>
                  <p className="text-sm text-kleda-text-muted mt-xs">{error}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => fetchItems()}>
                  Opnieuw proberen
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Warning banner for offline mode */}
        {!isLoading && error && items.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-kleda-md p-sm flex items-center gap-sm">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-yellow-800">{error}</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && items.length === 0 && (
          <Card>
            <CardContent className="py-xl">
              <div className="flex flex-col items-center justify-center gap-md text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-kleda-text-main">Alle voorinspecties gedaan!</p>
                  <p className="text-sm text-kleda-text-muted mt-xs">
                    Er zijn geen items die voorinspectie nodig hebben.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No search results */}
        {!isLoading && !error && items.length > 0 && filteredItems.length === 0 && (
          <Card>
            <CardContent className="py-lg">
              <div className="flex flex-col items-center justify-center gap-md text-center">
                <svg className="w-12 h-12 text-kleda-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <div>
                  <p className="font-medium text-kleda-text-main">Geen resultaten</p>
                  <p className="text-sm text-kleda-text-muted mt-xs">
                    Geen items gevonden voor "{searchQuery}"
                  </p>
                </div>
                {/* De lijst filtert alleen wat al geladen is. Staat het kleed
                    in een andere status, dan valt het buiten deze lijst en is
                    het hier per definitie onvindbaar. Doorsturen naar het
                    zoeken over alle kleden, met de zoekterm mee zodat er niet
                    opnieuw getypt hoeft te worden. */}
                <Link
                  to={`/zoek?q=${encodeURIComponent(searchQuery)}`}
                  className="text-sm font-medium text-kleda-primary underline"
                >
                  Zoek "{searchQuery}" in alle kleden
                </Link>
                <Button variant="ghost" size="sm" onClick={() => setSearchQuery('')}>
                  Wis zoekopdracht
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Grouped item cards (3 secties: Klant zegt nee / Wacht klant / Klaar) */}
        {!isLoading && groups.map(group => (
          group.items.length === 0 ? null : (
            <section key={group.key} className="space-y-sm">
              <div className="flex items-baseline gap-sm pt-sm">
                <h3 className="font-semibold text-kleda-text-main">
                  {group.emoji} {group.label}
                </h3>
                <span className="text-xs text-kleda-text-muted">
                  {group.items.length}
                </span>
              </div>
              <p className="text-xs text-kleda-text-muted -mt-xs">
                {group.description}
              </p>
              {group.items.map((item) => {
                const wait = group.key === 'wacht' ? formatWaitTime(item.risicoGemeldOp) : null;
                return (
                  <Card
                    key={item.itemId}
                    variant="elevated"
                    interactive
                    onClick={() => navigate(`/voorinspectie/${item.itemId}`)}
                    className="cursor-pointer"
                  >
                    <CardContent className="py-md">
                      <div className="flex items-start justify-between gap-md">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-sm mb-xs flex-wrap">
                            <h3 className="font-semibold text-kleda-text-main">
                              {item.itemName}
                            </h3>
                            {item.isKlacht && (
                              <Badge variant="default" className="text-xs bg-red-100 text-red-800">
                                Klacht
                              </Badge>
                            )}
                            {wait && (
                              <Badge variant="default" className="text-xs bg-yellow-100 text-yellow-800">
                                {wait}
                              </Badge>
                            )}
                            {group.key === 'nee' && (
                              <Badge variant="default" className="text-xs bg-red-100 text-red-800">
                                actie nodig
                              </Badge>
                            )}
                            {group.key === 'akkoord' && (
                              <Badge variant="default" className="text-xs bg-green-100 text-green-800">
                                klop + rond af
                              </Badge>
                            )}
                          </div>

                          <p className="text-sm text-kleda-text-main mb-xs">
                            {item.productName}
                          </p>

                          <div className="flex flex-wrap items-center gap-x-md gap-y-xs text-xs text-kleda-text-muted">
                            <span>{item.werkOrderName}</span>
                            <span>•</span>
                            <span>{formatDimensions(item)}</span>
                            <span>•</span>
                            <span>{formatDate(item.createdDate)}</span>
                          </div>

                          {(item.soort || item.merk) && (
                            <div className="flex flex-wrap gap-xs mt-sm">
                              {item.soort && (
                                <Badge variant="default" className="text-xs">
                                  {item.soort}
                                </Badge>
                              )}
                              {item.merk && (
                                <Badge variant="default" className="text-xs">
                                  {item.merk}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>

                        <svg className="w-5 h-5 text-kleda-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </section>
          )
        ))}
      </div>
    </div>
  );
}
